import { ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  PaginationProvider,
  SortProvider,
  NameSection,
  AdditionalInfoSection,
  DevelopInfoSection,
  MapSection,
  PropertyPreviewNavigationTabs,
  Spinner,
  useScreenResolution,
  InventorySectionProvider,
} from "dash-ui";
import styled from "styled-components";
import { GetPropertyByIdQuery } from "gql/generated.graphql";
import Container from "flow-components/Container/Container";

import SlideShowSection from "containers/properties/PropertyPreview/components/SlideShowSection/SlideShowSection";
import AmenitiesSection from "containers/properties/PropertyPreview/components/AmenitiesSection/AmenitiesSection";
import InventoryTableSection from "containers/properties/PropertyPreview/components/InventoryTableSection/InventoryTableSection";
import MarketingFilesSection from "containers/properties/PropertyPreview/components/MarketingFilesSection/MarketingFilesSection";
import { IContainerProps } from "flow-components/Container/Container.interfaces";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { GeneralInfoSection as PropertyGeneralInfoSection } from "v2/components/property-general-info-section";
import { useMetaDescription } from "common/hooks/metadata-tags/use-meta-description";
import { useMetaImage } from "common/hooks/metadata-tags/use-meta-image";
import { useMetaOgDescription } from "common/hooks/metadata-tags/use-meta-og-description";
import { useMetaOgTitle } from "common/hooks/metadata-tags/use-meta-og-title";

const MobileBannerPadding = styled(Flex)`
  @media ${({ theme }) => theme.devices.mobile} {
    margin-bottom: 128px;
  }
`;

function PropertyPreview({
  property,
  isViewAsClient,
  isPortfolioView,
  leftSideBar,
  loading,
  pageContainerProps = {},
  isSaleTeamVisible = true,
  isAddedToMediaKit,
  showMediaKit,
}: {
  property?: GetPropertyByIdQuery["getPropertyById"];
  isViewAsClient?: boolean;
  isPortfolioView?: boolean;
  loading?: boolean;
  leftSideBar?: ReactNode;
  pageContainerProps?: IContainerProps;
  isSaleTeamVisible?: boolean;
  isAddedToMediaKit?: boolean;
  showMediaKit?: boolean;
}) {
  const generalInfoRef = useRef(null);
  const additionalInfoRef = useRef(null);
  const marketingFilesInfoRef = useRef(null);
  const { t } = useTranslation();
  useBrowserTabTitle(
    `${property?.name || t("common:labels.loading")} ${t(
      "common:labels.dashSuffix"
    )}`
  );
  const {
    devices: { isMobile },
  } = useScreenResolution();

  // Add metadata tags
  useMetaDescription(property?.overview);
  useMetaImage(property?.mainPropertyMediaFile?.files[0]?.publicUrl);
  useMetaOgDescription(property?.overview);
  useMetaOgTitle(
    `${property?.name || t("common:labels.loading")} ${t(
      "common:labels.dashSuffix"
    )}`
  );

  return (
    <Container
      breadcrumbsProps={{ sticky: true }}
      withSidebar={!isViewAsClient}
      showContent
      {...pageContainerProps}
      className="bg-[#F8FBFD]"
    >
      <Flex
        flexDirection="column"
        flex={1}
        w="100%"
        pos="relative"
        minHeight={150}
        mb={[0, 14, 0, 0]}
      >
        {!property || loading ? (
          <Spinner size={32} fullContent bgColor="inherit" />
        ) : (
          <>
            <SlideShowSection
              property={property}
              isViewAsClient={isViewAsClient}
            />
            <NameSection property={property} />
            <Flex
              w="100%"
              style={
                !isMobile
                  ? {
                      maxWidth:
                        isPortfolioView && !isAddedToMediaKit
                          ? "100%"
                          : "calc(100% - 279px)",
                    }
                  : {}
              }
            >
              <Box w="100%">
                <PropertyPreviewNavigationTabs
                  isViewAsClient={isViewAsClient}
                  navigationElementsRefs={[
                    generalInfoRef,
                    additionalInfoRef,
                    marketingFilesInfoRef,
                  ]}
                />
                <Box w="100%">
                  <PropertyGeneralInfoSection
                    data={{
                      highlights: property.projectDetails,
                      overview: property.overview,
                    }}
                    containerRef={generalInfoRef}
                    isViewAsClient={isViewAsClient}
                  />

                  <AmenitiesSection
                    property={property}
                    isViewAsClient={isViewAsClient}
                  />
                  <PaginationProvider perPage={5}>
                    <SortProvider>
                      <InventorySectionProvider>
                        <InventoryTableSection
                          property={property}
                          isViewAsClient={isViewAsClient}
                          isPortfolioView={isPortfolioView}
                        />
                      </InventorySectionProvider>
                    </SortProvider>
                  </PaginationProvider>

                  <AdditionalInfoSection
                    property={property}
                    containerRef={additionalInfoRef}
                    isViewAsClient={!!isViewAsClient}
                  />

                  <MapSection property={property} />
                  {!isViewAsClient && (
                    <DevelopInfoSection
                      property={property}
                      isSaleTeamVisible={isSaleTeamVisible}
                    />
                  )}
                  <MarketingFilesSection
                    isPortfolioView={isPortfolioView}
                    containerRef={marketingFilesInfoRef}
                    isAddedToMediaKit={isAddedToMediaKit}
                    showMediaKit={showMediaKit}
                  />
                </Box>
              </Box>
              {leftSideBar}
            </Flex>
            <MobileBannerPadding w="100%" />
          </>
        )}
      </Flex>
    </Container>
  );
}
export default PropertyPreview;
